import { Injectable, inject } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { ApiClientService } from "../api-client.service";
import { ModelObserver } from "./model-observer";
import { CommandActionEnum } from "./models/command-action-enum";
import { CommandDto } from "./models/command-dto";
import { CommandResultDto } from "./models/command-result-dto";

export interface ICommand {
    $type: string;
}

@Injectable({
    providedIn: "root"
})
export class ModelActionService {
    commandQueue: CommandDto[] = [];
    observers: ModelObserver[] = [];
    skipNextCommand = false;
    transactionCommandQueue: ICommand[] = [];

    private readonly apiClient = inject(ApiClientService);

    addCommand<T extends object>(command: T, type: string) {
        this.pushCommand(command, null, CommandActionEnum.Trigger, type);
    }

    addNewChildModel(model: object, type: string, parentItem: any, entityIdPropertyName = "id") {
        if (!this.isBound(parentItem)) {
            throw new Error("Cannot add child item, parent needs to be bound/added first");
        }
        const observer = this.bindSingle(model, type, entityIdPropertyName, parentItem, null);
        this.handleModelAdd(observer, type, parentItem, null);
    }

    addNewModel(model: object, type: string, entityIdPropertyName = "id", properties: string[] = null) {
        const observer = this.bindSingle(model, type, entityIdPropertyName, null, properties);
        this.handleModelAdd(observer, type, null, properties);
    }

    addTransactionCommand<T extends ICommand>(command: T) {
        this.transactionCommandQueue.push(command);
    }

    bindChildModel(model: object, type: string, parentItem: any, entityIdPropertyName = "id") {
        if (model instanceof Array) {
            this.bindMany(model, type, entityIdPropertyName, parentItem, null);
        } else {
            this.bindSingle(model, type, entityIdPropertyName, parentItem, null);
        }
    }

    bindModel(model: object, type: string, entityIdPropertyName = "id", properties: string[] = null) {
        if (model instanceof Array) {
            this.bindMany(model, type, entityIdPropertyName, null, properties);
        } else {
            this.bindSingle(model, type, entityIdPropertyName, null, properties);
        }
    }

    pushCommandQueue(): Observable<any> {
        this.sortQueue();
        const subject = new Subject();
        const commandsToPush = this.commandQueue;
        this.commandQueue = [];

        // eslint-disable-next-line sonarjs/deprecation
        this.apiClient.post<CommandDto[], CommandResultDto[]>("cqs/commands", commandsToPush, false).subscribe(result => {
            this.handleResponse(result, commandsToPush);
            subject.next(result);
        }, (e) => {
            this.returnToQueue(commandsToPush);
            subject.error(e);
        });
        return subject.asObservable();
    }

    pushCommandQueueTransaction(): Observable<any> {
        const subject = new Subject();
        const commandsToPush = this.transactionCommandQueue;
        // eslint-disable-next-line sonarjs/deprecation
        this.apiClient.post<ICommand[], any>("cqs/transactioncommands", commandsToPush, false).subscribe(result => {
            subject.next(result);
        }, (e) => {
            subject.error(e);
        });
        this.transactionCommandQueue = [];
        return subject.asObservable();
    }

    removeModel(model: object, type: string, parentItem: any = null) {
        this.handleModelDelete(model, type, parentItem);
        this.removeFromObservers(model);
    }

    reset() {
        for (let i = 0; i < this.observers.length; i++) {
            // eslint-disable-next-line sonarjs/no-array-delete
            delete this.observers[i];
            this.observers = [];
        }
        for (let i = 0; i < this.commandQueue.length; i++) {
            // eslint-disable-next-line sonarjs/no-array-delete
            delete this.commandQueue[i];
            this.commandQueue = [];
        }
    }

    private bindMany(models: object[], type: string, entityIdPropertyName: string, parentItem: any, properties: string[]) {
        models.forEach(m => {
            this.bindSingle(m, type, entityIdPropertyName, parentItem, properties);
        });
    }

    private bindSingle(model: object, type: string, entityIdPropertyName: string, parentItem: any, properties: string[]): ModelObserver {
        const observer = new ModelObserver(model,
            (m, c) => this.handleModelChange(m, c, type, parentItem, properties), entityIdPropertyName, properties);
        this.observers.push(observer);
        return observer;
    }

    private clearCommands(commandId: string) {
        const existing = this.commandQueue.filter(cq => cq.commandId === commandId);
        existing.forEach(cmd => {
            const index = this.commandQueue.indexOf(cmd);
            this.commandQueue.splice(index, 1);
        });
    }

    private getCommandId(model: object): string {
        return model ? model[ModelObserver.observerIdPropertyName] : null;
    }

    private getCommandType(type, action: CommandActionEnum): string {
        const suffix = `${type}Command`;
        switch (action) {
            case CommandActionEnum.Create:
                return `Save${suffix}`;
            case CommandActionEnum.Modify:
                return `Save${suffix}`;
            case CommandActionEnum.Delete:
                return `Delete${suffix}`;
            case CommandActionEnum.Trigger:
                return suffix;
            default:
                return undefined;
        }
    }

    private getExistingCommand(id: string, type: string, action: CommandActionEnum): CommandDto {
        const existing = this.commandQueue.filter(cq => cq.commandId === id);
        if (existing.length === 0) {
            return null;
        }
        return existing[0];
    }

    private getId(model: object): string {
        return model ? model[model[ModelObserver.entityIdPropertyName]] : null;
    }

    private groomQueue(commandId, action: CommandActionEnum) {
        if (action === CommandActionEnum.Delete) {
            this.clearCommands(commandId);
        }
    }

    private handleModelAdd(observer: ModelObserver, type: string, parentItem: any, properties: string[]) {
        this.pushCommand(observer.model, observer.changes, CommandActionEnum.Create, type, parentItem, properties);
    }

    private handleModelChange(model: object, changes: object, type: string, parentItem: any, properties: string[]): Observable<any> {
        const sub = new BehaviorSubject(null);
        this.pushCommand(model, changes, CommandActionEnum.Modify, type, parentItem, properties);
        sub.complete();
        return sub.asObservable();
    }

    private handleModelDelete(model: object, type: string, parentItem: any = null) {
        if (this.isNew(model)) {
            const commandId = this.getCommandId(model);
            this.clearCommands(commandId);
        } else {
            this.pushCommand(model, null, CommandActionEnum.Delete, type, parentItem);
        }
    }

    private handleResponse(result: CommandResultDto[], commands: CommandDto[]) {
        commands.forEach(c => {
            if (c.changes == null) {
                return;
            }
            const receipt = result.filter(s => s.commandId === c.commandId)[0];

            for (const propertyName of Object.keys(c.changes)) {
                delete c.changes[propertyName];
            }

            for (const changedProperty of Object.keys(receipt.changes)) {
                this.skipNextCommand = true;
                c.payload[changedProperty] = receipt.changes[changedProperty];
                delete c.changes[changedProperty];
            }
        });
    }

    private isBound(model: object): boolean {
        return !!model[ModelObserver.observerIdPropertyName];
    }

    private isNew(model: object): boolean {
        return this.getId(model) === undefined;
    }

    private pushCommand(model: object, changes: object, action: CommandActionEnum, type: string, parentItem?: any, properties?: string[]) {
        if (this.skipNextCommand) {
            this.skipNextCommand = false;
            return undefined;
        }
        const commandId = this.getCommandId(model);
        const commandType = this.getCommandType(type, action);
        const parentId = this.getId(parentItem);
        const entityId = this.getId(model);
        const parentCommandId = this.getCommandId(parentItem);

        this.groomQueue(commandId, action);
        let command = this.getExistingCommand(commandId, commandType, action);
        if (command !== null) {
            return command;
        }

        command = {
            $type: commandType,
            action: action,
            changes: changes,
            commandId: commandId,
            entityId: entityId,
            parentCommandId: parentCommandId,
            parentId: parentId,
            payload: model,
            properties: properties
        };

        this.commandQueue.push(command);
        return command;
    }

    private removeFromObservers(model: object) {
        const matches = this.observers.filter(o => o.model === model);
        if (matches.length < 1) {
            return;
        }
        matches.forEach(m => {
            const index = this.observers.indexOf(m);
            // eslint-disable-next-line sonarjs/no-array-delete
            delete this.observers[index];
        });
    }

    private returnToQueue(commands: CommandDto[]) {
        const newQueue = commands;
        newQueue.push(...this.commandQueue);
        this.commandQueue = newQueue;
    }

    private sortQueue() {
        const parents = this.commandQueue.filter(q => q.parentId == null);
        const kids = this.commandQueue.filter(q => q.parentId != null);
        const sorted: CommandDto[] = [];
        sorted.push(...parents);
        kids.forEach(kid => {
            const parentIndex = sorted.findIndex(s => s.entityId === kid.parentId);
            if (parentIndex >= 0) {
                sorted.splice(parentIndex + 1, 0, kid);
            } else {
                sorted.push(kid);
            }
        });
        this.commandQueue = sorted;
    }
}
